import { forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { keyframes } from 'styled-components/macro';
import { themeGet } from '@styled-system/theme-get';
import css from '@styled-system/css';
import { grid, variant, GridProps } from 'styled-system';

export const ButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

export const ButtonsGrid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;

  ${grid};
`;

export const ButtonsPosition = styled.div<{
  position?: string;
}>`
  position: absolute;
  top: 0;
  left: ${(props) => (props?.position === 'left' ? 0 : 'auto')};
  right: ${(props) => (props?.position === 'right' ? 0 : 'auto')};

  @media ${themeGet('mediaQueries.desktopDown')} {
    position: relative;
    display: flex;
    margin-bottom: 20px;
    gap: 10px;
  }

  button {
    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }
`;

export const ButtonNavLink = styled(NavLink)`
  display: flex;
  align-content: center;
  justify-content: center;
  width: 42px;
  height: 42px;
`;

export const ClearButton = styled.button`
  display: flex;
  align-content: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  margin: 0;
  outline: none;
  background-color: transparent;
`;

type VariantNames = 'default' | 'secondary' | 'outlined';

type Sizes = 'small' | 'middle' | 'big';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  as?: React.ElementType | keyof JSX.IntrinsicElements;
  href?: string;
  target?: string;
  fullwidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  variant?: VariantNames;
  size?: Sizes;
  alignSelf?: 'center' | 'start' | 'flex-start' | 'flex-end';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const StyledButton = styled.button<ButtonProps>(
  (props) =>
    css({
      width: props?.fullwidth ? '100%' : 'auto',
      px: '25px',
      py: '12px',
      fontSize: ['base'],
      fontWeight: 'button',
      alignSelf: props?.alignSelf ? props.alignSelf : 'flex-start',
      cursor: props?.disabled ? 'not-allowed' : 'pointer',
      color: 'text.light',
      bg: 'primary.regular',
      whiteSpace: 'nowrap',
      transition: 'all 0.3s ease',
      borderRadius: 'base',
      lineHeight: 'button',

      '&:hover': {
        bg: 'button.hover'
      },

      '&:active': {
        bg: 'button.active'
      },

      '&:disabled': {
        bg: 'button.disabled'
      }
    }),
  {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    textDecoration: 'none',
    fontFamily: 'inherit',
    appearance: 'none',

    '> svg': {
      marginRight: '12px'
    },

    '&:focus': {
      outline: 'none'
    }
  },
  variant({
    variants: {
      default: {
        py: '7px !important'
      },
      secondary: {
        color: 'text.light',
        bg: 'button.variant.secondary.regular',

        '&:hover': {
          bg: 'button.variant.secondary.hover'
        },

        '&:active': {
          bg: 'button.variant.secondary.active'
        },

        '&:disabled': {
          bg: 'button.disabled'
        }
      },
      outlined: {
        p: '3px 30px',
        bg: 'transparent',
        border: '2px solid',
        borderColor: 'button.regular',
        borderRadius: 'base',
        color: 'button.regular',
        fontSize: 'sm',
        fontWeight: 'semi',
        lineHeight: '18px',

        '&:hover': {
          bg: 'button.regular',
          color: 'white'
        },

        '&:disabled': {
          bg: 'button.disabled',
          borderColor: 'button.disabled',
          color: 'white'
        }
      }
    }
  }),
  variant({
    prop: 'size',
    variants: {
      small: {
        px: '9px',
        py: '5px',
        fontSize: 'xs',
        fontWeight: 'semi',
        textTransform: 'uppercase',
        borderRadius: 'middle',
        lineHeight: 'heading',
        color: 'primary.extra'
      },
      middle: {
        px: '20px',
        py: '6px',
        fontSize: 'sm',
        lineHeight: '24px'
      },
      big: {
        px: '20px',
        py: '10px',
        fontSize: 'sm',
        fontWeight: 'bold'
      }
    }
  })
);

const rotate = keyframes`
  from {transform: rotate(0deg);}
  to {transform: rotate(360deg);}
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  margin-left: 10px;
  border: 2px solid ${(props) => props.theme.colors.text.light};
  border-top: 2px solid
    ${(props) => (props?.color ? props.color : 'transparent')};
  border-radius: 50%;
  transition-property: transform;
  animation-name: ${rotate};
  animation-duration: 1.2s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, disabled, loading = false, ...props }, ref) => (
    <StyledButton ref={ref} {...props} disabled={disabled}>
      {children}
      {loading && <Spinner />}
    </StyledButton>
  )
);
